import React, { useState, useEffect } from "react";
import axios from "axios";
import { User, Mail, Phone, Building2, Briefcase, Calendar, MapPin, AlertCircle, Save, Check } from "lucide-react";
import { useTheme } from "context/ThemeContext";

import { apiBaseUrl } from '../../../services/config';
const API = apiBaseUrl;

export default function EmployeeProfile() {
  const { T } = useTheme();
  const userData   = JSON.parse(sessionStorage.getItem("platform_user") || "{}");
  const platformId = userData.id;
  const token      = sessionStorage.getItem("platform_token") || "";
  const ax         = { headers:{ Authorization:`Bearer ${token}` } };

  const [profile,  setProfile]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [form,     setForm]     = useState({ phone:"", address:"", emergency_contact:"" });

  useEffect(() => {
    if (!platformId) return;
    axios.get(`${API}/employee-portal/profile?platform_user_id=${platformId}`, ax)
      .then(r => {
        setProfile(r.data);
        const e = r.data.employee || {};
        setForm({ phone:e.phone||"", address:e.address||"", emergency_contact:e.emergency_contact||"" });
      })
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, [platformId]);

  const save = async () => {
    if (!profile?.employee?.id) return;
    setSaving(true);
    try {
      const emp = profile.employee;
      await axios.put(`${API}/employees/${emp.id}`, {
        first_name: profile.first_name, last_name: profile.last_name,
        email: profile.email, phone: form.phone,
        address: form.address, emergency_contact: form.emergency_contact,
        department_id: emp.department_id, designation_id: emp.designation_id,
        status: "Active",
      }, ax);
      setSaved(true); setEditing(false);
      setTimeout(() => setSaved(false), 3000);
      setProfile(p => ({ ...p, employee: { ...p.employee, ...form } }));
    } catch(e) { alert(e.response?.data?.detail || "Error saving"); }
    finally { setSaving(false); }
  };

  const inp = { width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, fontSize:13, padding:"9px 12px", outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box" };
  const card = { background:T.card, border:`1px solid ${T.border}`, borderRadius:12, fontFamily:"'Outfit',sans-serif", overflow:"hidden" };
  const lbl  = { fontSize:11, color:T.muted, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4, display:"block" };

  if (loading) return <div style={{ padding:60, textAlign:"center", color:T.muted, fontFamily:"Outfit,sans-serif" }}>Loading profile…</div>;

  const emp = profile?.employee;

  return (
    <div style={{ fontFamily:"'Outfit',sans-serif", color:T.text, maxWidth:800 }}>
      <div style={{ marginBottom:22 }}>
        <div style={{ fontSize:11, color:T.cyan, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>MY ACCOUNT</div>
        <h2 style={{ fontSize:22, fontWeight:800, margin:"0 0 4px" }}>My Profile</h2>
        <p style={{ fontSize:13, color:T.muted, margin:0 }}>View your employee details. Contact HR to update name, designation or department.</p>
      </div>

      {/* Avatar + name card */}
      <div style={{ ...card, marginBottom:16, padding:24 }}>
        <div style={{ display:"flex", gap:20, alignItems:"center", flexWrap:"wrap" }}>
          <div style={{ width:72, height:72, borderRadius:"50%", background:userData.avatar_color||"#00C8FF", display:"flex", alignItems:"center", justifyContent:"center", fontSize:24, fontWeight:800, color:"#fff", flexShrink:0 }}>
            {(profile?.first_name?.[0]||"")+(profile?.last_name?.[0]||"")}
          </div>
          <div>
            <div style={{ fontSize:22, fontWeight:800, color:T.text, marginBottom:4 }}>{profile?.first_name} {profile?.last_name}</div>
            <div style={{ display:"flex", gap:12, flexWrap:"wrap", fontSize:13, color:T.muted }}>
              {emp?.employee_code && <span style={{ fontFamily:"JetBrains Mono,monospace", color:T.cyan }}>{emp.employee_code}</span>}
              {emp?.designation && <span>{emp.designation}</span>}
              {emp?.department && <span>{emp.department}</span>}
            </div>
          </div>
          <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
            {saved && <span style={{ fontSize:12, color:T.green, display:"flex", alignItems:"center", gap:5 }}><Check size={13}/>Saved</span>}
            {!editing ? (
              <button onClick={() => setEditing(true)} style={{ fontSize:13, padding:"8px 16px", borderRadius:8, border:`1px solid ${T.cyan}33`, background:`${T.cyan}0F`, color:T.cyan, cursor:"pointer", fontFamily:"Outfit,sans-serif" }}>Edit Contact Info</button>
            ) : (
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={() => setEditing(false)} style={{ fontSize:13, padding:"8px 14px", borderRadius:8, border:`1px solid ${T.toggle_bg}`, background:T.inp_bg, color:T.muted, cursor:"pointer", fontFamily:"Outfit,sans-serif" }}>Cancel</button>
                <button onClick={save} disabled={saving} style={{ fontSize:13, padding:"8px 16px", borderRadius:8, border:"none", background:`linear-gradient(135deg,${T.cyan},${T.purple})`, color:"#fff", cursor:"pointer", fontFamily:"Outfit,sans-serif", fontWeight:600, display:"flex", alignItems:"center", gap:6 }}>
                  <Save size={13}/>{saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
        {/* Read-only info */}
        <div style={{ ...card, padding:20 }}>
          <div style={{ fontSize:12, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:14 }}>Employment Details</div>
          {[
            { icon:Mail,       label:"Email",             val:profile?.email },
            { icon:Building2,  label:"Department",        val:emp?.department },
            { icon:Briefcase,  label:"Designation",       val:emp?.designation },
            { icon:Calendar,   label:"Date of Joining",   val:emp?.date_of_joining },
          ].map(({ icon:Icon, label, val }) => (
            <div key={label} style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
              <div style={{ width:32, height:32, borderRadius:8, background:`${T.cyan}14`, display:"flex", alignItems:"center", justifyContent:"center", color:T.cyan, flexShrink:0 }}>
                <Icon size={14}/>
              </div>
              <div>
                <div style={{ fontSize:10.5, color:T.muted, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:1 }}>{label}</div>
                <div style={{ fontSize:13, color:T.text }}>{val || "—"}</div>
              </div>
            </div>
          ))}
          <div style={{ marginTop:12, padding:"8px 12px", background:`${T.amber}0F`, border:`1px solid ${T.amber}1F`, borderRadius:8, fontSize:12, color:T.amber, display:"flex", gap:7, alignItems:"flex-start" }}>
            <AlertCircle size={13} style={{ flexShrink:0, marginTop:1 }}/> Contact HR to update name, email, department or designation.
          </div>
        </div>

        {/* Editable info */}
        <div style={{ ...card, padding:20 }}>
          <div style={{ fontSize:12, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:14 }}>Contact Info <span style={{ fontSize:10, color:T.green }}>(You can edit these)</span></div>
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            {[
              { label:"Phone Number", field:"phone",             icon:Phone,       placeholder:"+91 98765 43210" },
              { label:"Address",      field:"address",           icon:MapPin,      placeholder:"Your address", multiline:true },
              { label:"Emergency Contact", field:"emergency_contact", icon:AlertCircle, placeholder:"Name & phone number" },
            ].map(({ label, field, icon:Icon, placeholder, multiline }) => (
              <div key={field}>
                <label style={lbl}>{label}</label>
                {multiline ? (
                  <textarea value={form[field]} onChange={e => setForm(p=>({...p,[field]:e.target.value}))}
                    rows={2} placeholder={placeholder} disabled={!editing}
                    style={{ ...inp, resize:"vertical", opacity:editing?1:0.6 }}/>
                ) : (
                  <input value={form[field]} onChange={e => setForm(p=>({...p,[field]:e.target.value}))}
                    placeholder={placeholder} disabled={!editing}
                    style={{ ...inp, opacity:editing?1:0.6 }}/>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Manager info */}
        {profile?.manager && (
          <div style={{ ...card, padding:20 }}>
            <div style={{ fontSize:12, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:14 }}>Reporting To</div>
            <div style={{ display:"flex", gap:12, alignItems:"center" }}>
              <div style={{ width:40, height:40, borderRadius:"50%", background:T.purple, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:"#fff" }}>
                {profile.manager.name.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2)}
              </div>
              <div>
                <div style={{ fontSize:14, fontWeight:600 }}>{profile.manager.name}</div>
                <div style={{ fontSize:12, color:T.muted }}>{profile.manager.role?.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}</div>
                <div style={{ fontSize:12, color:T.dim }}>{profile.manager.email}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
